import { useCallback, useState } from "react";
import { Button, Popover, PopoverContent, PopoverTrigger } from "@repo/ui";
import { ChevronDown, FileText, Loader2 } from "lucide-react";

/**
 * Menu de téléchargement des rapports d'une évaluation — un par destinataire
 * (candidat, recruteur…), chacun en PDF ou en Word.
 *
 * Les rapports sont produits par le SERVEUR (`POST /api/reports`) à partir des
 * templates Word officiels. Le document n'est stocké nulle part : il arrive
 * dans la réponse HTTP, devient un blob le temps du clic, et l'URL objet est
 * révoquée aussitôt — rien ne reste ni côté Convex ni côté navigateur.
 *
 * `rapports`, `token` et `convexUrl` sont des PROPS : ce package est du rendu
 * sans client Convex (cf. `index.ts`). L'appelant les obtient par
 * `api.reports.rapportsDisponibles`, `useAuthToken()` et
 * `process.env.BUN_PUBLIC_CONVEX_URL` — ce dernier n'étant remplacé qu'au bundle
 * de chaque app, sa lecture depuis un package partagé donnerait une valeur vide.
 */

export type DestinataireRapport = "candidat" | "recruteur";

/** Mêmes valeurs que `FORMATS` de `convex/lib/rapports/catalogue.ts`. */
export type FormatRapport = "pdf" | "docx";

const FORMATS: { format: FormatRapport; libelle: string }[] = [
  { format: "pdf", libelle: "PDF" },
  { format: "docx", libelle: "Word" },
];

export interface RapportPropose {
  destinataire: DestinataireRapport;
  /** Faux tant que la source n'est pas prête (analyse, référentiel…). */
  disponible: boolean;
}

const LIBELLES: Record<DestinataireRapport, { titre: string; detail: string }> =
  {
    candidat: {
      titre: "Rapport candidat",
      detail: "À remettre au candidat",
    },
    recruteur: {
      titre: "Rapport recruteur",
      detail: "Document de travail interne",
    },
  };

const MOTIF_INDISPONIBLE =
  "Pas encore disponible : analyse non terminée ou référentiel non finalisé";

/**
 * L'URL du SITE, celle qui sert les `httpAction` — pas celle du client temps
 * réel. Déduite de l'URL Convex de l'app plutôt que déclarée à part, pour qu'il
 * n'y ait pas deux variables à tenir cohérentes.
 *
 * Deux conventions, selon le déploiement :
 *   - Convex Cloud : `xxx.convex.cloud` → `xxx.convex.site` ;
 *   - backend local (`convex dev`) : même hôte, port + 1 (3210 → 3211).
 */
export function urlSiteConvex(convexUrl: string): string {
  if (convexUrl.includes(".convex.cloud")) {
    return convexUrl.replace(".convex.cloud", ".convex.site");
  }
  try {
    const u = new URL(convexUrl);
    if (u.port) {
      u.port = String(Number(u.port) + 1);
      return u.origin;
    }
  } catch {
    // URL non analysable : on la renvoie telle quelle, l'appel échouera avec un
    // message clair plutôt que sur une URL silencieusement bricolée.
  }
  return convexUrl;
}

function nomFichierDepuisEnTete(entete: string | null, defaut: string): string {
  const trouve = /filename="([^"]+)"/.exec(entete ?? "")?.[1];
  return trouve || defaut;
}

export function RapportsMenu({
  evaluationId,
  rapports,
  candidatInfo,
  token,
  convexUrl,
}: {
  evaluationId: string;
  /** `api.reports.rapportsDisponibles` — vide ou en chargement : pas de menu. */
  rapports: RapportPropose[] | undefined;
  /** Pour le nom de fichier de repli, si la réponse n'en donne pas. */
  candidatInfo: { prenom?: string; nom?: string };
  /** Jeton d'auth de l'app appelante (`useAuthToken()`). */
  token: string | null;
  /** `BUN_PUBLIC_CONVEX_URL` de l'app appelante. */
  convexUrl: string;
}) {
  const [ouvert, setOuvert] = useState(false);
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const telecharger = useCallback(
    async (destinataire: DestinataireRapport, format: FormatRapport) => {
      setOuvert(false);
      setErreur(null);
      setEnCours(true);
      let urlObjet: string | null = null;
      try {
        const reponse = await fetch(`${urlSiteConvex(convexUrl)}/api/reports`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token ?? ""}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ evaluationId, destinataire, format }),
        });

        if (!reponse.ok) {
          const detail = (await reponse.json().catch(() => null)) as {
            erreur?: string;
          } | null;
          throw new Error(detail?.erreur ?? `Échec (${reponse.status})`);
        }

        const blob = await reponse.blob();
        urlObjet = URL.createObjectURL(blob);
        const lien = document.createElement("a");
        lien.href = urlObjet;
        lien.download = nomFichierDepuisEnTete(
          reponse.headers.get("Content-Disposition"),
          `Rapport_${destinataire}_${candidatInfo.prenom ?? ""}_${candidatInfo.nom ?? ""}.${format}`,
        );
        document.body.appendChild(lien);
        lien.click();
        lien.remove();
      } catch (e) {
        setErreur(
          e instanceof Error ? e.message : "Génération du rapport en échec",
        );
      } finally {
        // Révoqué dans tous les cas : le blob ne doit pas survivre au clic.
        if (urlObjet) URL.revokeObjectURL(urlObjet);
        setEnCours(false);
      }
    },
    [token, convexUrl, evaluationId, candidatInfo.prenom, candidatInfo.nom],
  );

  if (!rapports || rapports.length === 0) return null;

  return (
    <div className="flex flex-col items-end gap-1">
      <Popover open={ouvert} onOpenChange={setOuvert}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            disabled={enCours || !token}
          >
            {enCours ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <FileText className="h-4 w-4 mr-2" />
            )}
            {enCours ? "Génération…" : "Rapports"}
            <ChevronDown className="h-4 w-4 ml-1" />
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-1">
          <ul role="menu" className="flex flex-col">
            {rapports.map(({ destinataire, disponible }) => (
              <li
                key={destinataire}
                role="none"
                title={disponible ? undefined : MOTIF_INDISPONIBLE}
                className={`flex items-center justify-between gap-3 rounded-surface px-3 py-2 text-sm ${disponible ? "" : "opacity-50"}`}
              >
                <span>
                  <span className="block font-medium">
                    {LIBELLES[destinataire].titre}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    {disponible
                      ? LIBELLES[destinataire].detail
                      : "Pas encore disponible"}
                  </span>
                </span>
                <span className="flex shrink-0 gap-1">
                  {FORMATS.map(({ format, libelle }) => (
                    <Button
                      key={format}
                      type="button"
                      role="menuitem"
                      variant="outline"
                      size="sm"
                      disabled={!disponible}
                      aria-label={`${LIBELLES[destinataire].titre} en ${libelle}`}
                      onClick={() => void telecharger(destinataire, format)}
                      className="disabled:cursor-not-allowed"
                    >
                      {libelle}
                    </Button>
                  ))}
                </span>
              </li>
            ))}
          </ul>
        </PopoverContent>
      </Popover>
      {erreur && <span className="text-xs text-destructive">{erreur}</span>}
    </div>
  );
}
